import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@peacecover/component';
import { ArrowLeft } from '@peacecover/icon';
import { useGetBuddyPreferences } from '../../domains/findBuddy/ui/hooks/useGetBuddyPreferences';
import { getRandomBackgroundColor } from './utils/getRandomBackgroundColor';
import FanBuddyProfileCard from './components/FanBuddyProfileCard';
import GradientLoadingSpinner from '../components/GradientLoadingSpinner';
import type { components } from '../../types/schema';

type BuddyProfileCard = components['schemas']['ApiV2BuddyProfileCard'];

const FanBuddyIntroPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [backgroundColor, setBackgroundColor] = useState('#5DFFC5');

  const { data: buddyPreferences, isLoading: isLoadingBuddyPreferences } = useGetBuddyPreferences();

  const hasBuddyPreference = buddyPreferences?.cardStatus === 'v2';

  const handleNavigate = () => {
    navigate(`/fan-buddy/setting?backgroundColor=${encodeURIComponent(backgroundColor)}`);
  };

  const sampleBuddyProfileCard: BuddyProfileCard = {
    isTheSameAge: true,
    nickname: 'FanHearts',
    likedArtists: [
      { artistId: '1', shortName: '팬덤이름', name: '팬덤이름', fandomName: '팬덤이름' }
    ],
    likedMembers: [
      { memberId: '1', name: '토미' },
      { memberId: '2', name: '이안' },
      { memberId: '3', name: '제이' }
    ],
    fanCareerLevel: 'veteran',
    stanningStyle: 'homebody',
    affectionType: 'romantic',
    fantasyType: 'non_rps',
    fandomSocialStyle: 'solo',
    keywords: [
      { tagId: '1', name: '아침형인간', category: 'keyword', taggingsCount: 1 },
      { tagId: '2', name: '전화싫어', category: 'keyword', taggingsCount: 1 },
      { tagId: '3', name: '알콜쓰레기', category: 'keyword', taggingsCount: 1 }
    ],
    backgroundColor: backgroundColor,
    thumbnailUrl: '/assets/fanBuddy/example_profile.png',
    instagramId: 'fanhearts.official',
    twitterId: 'PocapanOfficial',
    cardStatus: 'v2',
    userProfileId: 'sample-user-profile-id',
    gender: 'female',
    isMutualFollow: true
  };

  if (isLoadingBuddyPreferences) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center">
        <GradientLoadingSpinner size={36} />
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-[env(safe-area-inset-top)]">
      <Header />
      <div className="px-[24px] flex flex-col items-center py-[10px] mt-[34px] gap-[14px]">
        <h1 className="text-[26px] font-bold leading-[31px] text-text-main-lv1 text-center whitespace-pre-line">
          {hasBuddyPreference
            ? t('FanBuddyIntro.recreateTitle')
            : t('FanBuddyIntro.initialCreateTitle')}
        </h1>
        <p className="text-[15px] font-medium text-text-main-lv4 leading-[18px] text-center">
          {hasBuddyPreference
            ? t('FanBuddyIntro.recreateDescription')
            : t('FanBuddyIntro.initialCreateDescription')}
        </p>
      </div>
      <div
        className={`flex flex-col items-center justify-center ${hasBuddyPreference ? 'mt-[62px]' : 'mt-[42px]'}`}
      >
        <FanBuddyProfileCard
          size="tiny"
          buddyProfileCard={sampleBuddyProfileCard}
          isHiddenTag={true}
        />
        <button
          type="button"
          className="mt-[20px] text-[14px] font-medium leading-[17px] underline underline-offset-2 text-text-main-lv4"
          onClick={() => setBackgroundColor(getRandomBackgroundColor())}
        >
          {t('FanBuddyIntro.changeBackgroundColor')}
        </button>
      </div>
      <div className="fixed bottom-0 left-0 right-0 px-[24px] pb-[calc(16px+env(safe-area-inset-bottom))]">
        <Button
          size="large"
          btnColor="black"
          className="w-full text-[17px] font-semibold dark:bg-text-main-lv1 dark:text-pure-black"
          onClick={handleNavigate}
        >
          {t('FanBuddyIntro.createProfileCard')}
        </Button>
      </div>
    </div>
  );
};

export default FanBuddyIntroPage;

const Header = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const redirectPath = location.state?.redirectPath;

  const handleBack = () => {
    if (redirectPath) {
      navigate(redirectPath, { replace: true });
    } else {
      navigate('/home', { replace: true });
    }
  };
  return (
    <header className="px-[16px] py-[12px] flex items-center justify-center relative w-full">
      <button type="button" onClick={handleBack}>
        <ArrowLeft
          size={24}
          className="text-text-main-lv1 absolute left-[16px] top-[50%] translate-y-[-50%]"
        />
      </button>
      <p className="text-text-main-lv1 text-[17px] font-bold leading-[20px]">
        {t('FanBuddyIntro.title')}
      </p>
    </header>
  );
};
